import dayjs from "dayjs";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  EVENT_IMAGE_MAX_FILE_SIZE,
  STUDYGROUP_VISIBILITY_SEARCH_DISABLED,
} from "@/constants";
import { AcceptParticipantsContent } from "@/features/study-group-events/components/AcceptParticipantsDialog";
import {
  DefaultFooter,
  EventDescription,
  EventHero,
  EventSidebar,
} from "@/features/study-group-events/components/EventDetailParts";
import { ReminderEmailDialog } from "@/features/study-group-events/components/ReminderEmailDialog";
import { ScopeSelectionContent } from "@/features/study-group-events/components/ScopeSelectionModal";
import { SendEmailNowDialog } from "@/features/study-group-events/components/SendEmailNowDialog";
import { useEventAttendance } from "@/features/study-group-events/components/useEventAttendance";
import {
  buildEventDateLabel,
  pickPrimaryImageGuid,
} from "@/features/study-group-events/utils/eventDisplay";
import { usePropagateReminders } from "@/hooks/usePropagateReminders";
import { usePublicFileByGuid } from "@/hooks/usePublicFileByGuid";
import { useRemoveEventFile } from "@/hooks/useRemoveEventFile";
import { useReplaceEventFile } from "@/hooks/useReplaceEventFile";
import { toast } from "@/hooks/useToast";
import type { EventApiResponse, RecurrenceScope } from "@/lib/humhub-api/types";
import { formatRecurrencePattern } from "@/lib/utils/event/recurrence";
import {
  copyEventLinkToClipboard,
  formatEventLocationDisplay,
} from "@/lib/utils/eventUtils";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

interface EventDetailContentProps {
  event: EventApiResponse;
  onEdit?: (event: EventApiResponse, eventApiData: EventApiResponse) => void;
  onDuplicate?: (
    event: EventApiResponse,
    eventApiData: EventApiResponse,
  ) => void;
  onDelete?: (
    event: EventApiResponse,
    eventApiData: EventApiResponse,
    scope?: RecurrenceScope,
  ) => void;
  onDataRefresh?: () => void;
  renderFooter?: (event: EventApiResponse) => React.ReactNode;
  hideFooter?: boolean;
  showDuplicateButton?: boolean;
  onStudyGroupClick?: (studyGroup: EventApiResponse["container"]) => void;
  showLocationDetails?: boolean;
}

export const EventDetailContent: React.FC<EventDetailContentProps> = ({
  event,
  onEdit,
  onDuplicate,
  onDelete,
  onDataRefresh,
  renderFooter,
  hideFooter = false,
  showDuplicateButton = false,
  onStudyGroupClick,
  showLocationDetails = false,
}) => {
  const [showFullDescription, setShowFullDescription] =
    useState<boolean>(false);
  const [isReminderEmailDialogOpen, setIsReminderEmailDialogOpen] =
    useState<boolean>(false);
  const [isSendEmailNowDialogOpen, setIsSendEmailNowDialogOpen] =
    useState<boolean>(false);
  // Large event management flows stay inside the event detail modal instead of
  // opening a nested modal. This avoids z-index ordering issues between modals.
  const [activeView, setActiveView] = useState<"detail" | "attendance">(
    "detail",
  );
  const [isParticipantScopeDialogOpen, setIsParticipantScopeDialogOpen] =
    useState<boolean>(false);
  const [isFileScopeDialogOpen, setIsFileScopeDialogOpen] =
    useState<boolean>(false);
  const [isFileDeleteScopeDialogOpen, setIsFileDeleteScopeDialogOpen] =
    useState<boolean>(false);
  const [isFileDeleteConfirmOpen, setIsFileDeleteConfirmOpen] =
    useState<boolean>(false);
  const [acceptParticipantsInitialTab, setAcceptParticipantsInitialTab] =
    useState<"available" | "attending">("available");
  const [hasReminderUnsavedChanges, setHasReminderUnsavedChanges] =
    useState<boolean>(false);
  const [hasSendEmailUnsavedChanges, setHasSendEmailUnsavedChanges] =
    useState<boolean>(false);
  const [showReminderUnsavedWarning, setShowReminderUnsavedWarning] =
    useState<boolean>(false);
  const [showSendEmailUnsavedWarning, setShowSendEmailUnsavedWarning] =
    useState<boolean>(false);
  const [isDeleteScopeDialogOpen, setIsDeleteScopeDialogOpen] =
    useState<boolean>(false);
  const [deleteScope, setDeleteScope] = useState<RecurrenceScope>("single");
  const userInfo = useGlobalUserInfo();
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    isLoading: replaceFileLoading,
    error: replaceFileError,
    replaceFile,
    isSuccess: replaceFileSuccess,
  } = useReplaceEventFile();
  const {
    isLoading: removeFileLoading,
    error: removeFileError,
    removeFile,
    isSuccess: removeFileSuccess,
  } = useRemoveEventFile();

  const primaryImageFile = useMemo(() => {
    const files = event?.content?.files ?? [];
    return (
      files.find((file) => file.category === 1) ??
      files.find((file) => file.mime_type?.startsWith("image/")) ??
      files.find((file) => file.guid) ??
      null
    );
  }, [event?.content?.files]);
  const fileGuid = primaryImageFile?.guid;
  const {
    fileBlobUrl,
    isLoading: imageLoading,
    error: imageError,
  } = usePublicFileByGuid(fileGuid);

  const [fileError, setFileError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [fileScope, setFileScope] = useState<RecurrenceScope>("single");
  const [fileDeleteScope, setFileDeleteScope] =
    useState<RecurrenceScope>("single");

  const ownerCreatedById = event?.content?.metadata?.created_by?.id;
  const isOwner = Boolean(
    event?.is_owner ||
      (userInfo &&
        ownerCreatedById != null &&
        String(ownerCreatedById) === String(userInfo.id)),
  );
  const [participantScope, setParticipantScope] =
    useState<RecurrenceScope>("single");
  const [pendingParticipantAction, setPendingParticipantAction] = useState<{
    type: "accept" | "decline";
    ids: number[];
  } | null>(null);
  const [isAttendScopeDialogOpen, setIsAttendScopeDialogOpen] =
    useState<boolean>(false);
  const [attendScope, setAttendScope] = useState<RecurrenceScope>("single");
  const { propagateReminders } = usePropagateReminders();
  const isDraft = event?.is_draft === 1;
  const isSearchDisabledGroup =
    event?.container?.visibility === STUDYGROUP_VISIBILITY_SEARCH_DISABLED;
  const shouldFetchMembership = Boolean(
    userInfo &&
      event?.container?.id != null &&
      (isSearchDisabledGroup || onEdit || onDelete),
  );
  const primaryImageGuid = useMemo(
    () => pickPrimaryImageGuid(event?.content?.files),
    [event?.content?.files],
  );

  const {
    isAttending,
    respondLoading,
    respondError,
    handleAttendClick,
    attendingParticipants,
    uniqueReminders,
    canManageEvent,
    shouldRenderAttendCta,
    handleAcceptParticipants,
    handleDeclineParticipants,
    acceptAllParticipantsLoading,
    declineParticipantsLoading,
    canBulkAcceptParticipants,
  } = useEventAttendance({ event, onDataRefresh, shouldFetchMembership });

  const participantMutationLoading =
    acceptAllParticipantsLoading || declineParticipantsLoading;

  const scopeOptionLabels = useMemo(() => {
    const recurrenceRootStart =
      event?.recurrence_root_start_datetime ??
      event?.recurrence?.start_date ??
      null;
    const formattedStartForAll = recurrenceRootStart
      ? dayjs(recurrenceRootStart).format("YYYY-MM-DD")
      : event?.start_datetime
        ? dayjs(event.start_datetime).format("YYYY-MM-DD")
        : null;
    const formattedStartForFuture = event?.start_datetime
      ? dayjs(event.start_datetime).format("YYYY-MM-DD")
      : formattedStartForAll;
    const recurrenceUntil = event?.recurrence?.until;
    const formattedUntil = recurrenceUntil
      ? dayjs(recurrenceUntil).format("YYYY-MM-DD")
      : null;

    return {
      future:
        event?.is_parent === true
          ? undefined
          : formattedStartForFuture && formattedUntil
            ? `${t("common.scopeFuture")}`
            : formattedStartForFuture
              ? `${t("common.scopeFuture")}`
              : undefined,
      all:
        formattedStartForAll && formattedUntil
          ? `${t("common.scopeAll")}`
          : formattedStartForAll
            ? `${t("common.scopeAll")}`
            : undefined,
    } as Partial<Record<RecurrenceScope, string>>;
  }, [event?.start_datetime, event?.recurrence, t, event]);

  const scopeOptionsForModal = useMemo<RecurrenceScope[]>(() => {
    if (!event?.recurrence) return ["single"];
    return event.is_parent === true
      ? ["single", "all"]
      : ["single", "future", "all"];
  }, [event]);

  const scopeModalDescription = useMemo(() => {
    if (!event?.recurrence) return undefined;
    const summary = formatRecurrencePattern(event.recurrence, t);
    if (!summary) return undefined;
    const recurrenceStart =
      event.recurrence?.start_date ??
      (event.recurrence_root_start_datetime
        ? dayjs(event.recurrence_root_start_datetime).format("YYYY-MM-DD")
        : event.start_datetime
          ? dayjs(event.start_datetime).format("YYYY-MM-DD")
          : "");
    const recurrenceUntil =
      event.recurrence?.until && dayjs(event.recurrence.until).isValid()
        ? dayjs(event.recurrence.until).format("YYYY-MM-DD")
        : event.recurrence?.until;
    const range =
      recurrenceStart && recurrenceUntil
        ? `${recurrenceStart}~${recurrenceUntil}`
        : recurrenceStart
          ? `${recurrenceStart}~`
          : undefined;
    return t("eventManage.scopeSelectionDescriptionWithRecurrence", {
      summary,
      range: range ?? "",
    });
  }, [event, t]);

  useEffect(() => {
    if ((replaceFileSuccess || removeFileSuccess) && onDataRefresh) {
      onDataRefresh();
    }
  }, [replaceFileSuccess, removeFileSuccess, onDataRefresh]);

  const handleEdit = () => {
    if (onEdit) onEdit(event, event);
  };

  const handleDuplicate = () => {
    if (onDuplicate) onDuplicate(event, event);
  };

  const handleDelete = () => {
    if (!onDelete) return;

    if (event?.recurrence) {
      setDeleteScope("single");
      setIsDeleteScopeDialogOpen(true);
      return;
    }

    onDelete(event, event);
  };

  const handleShowAttendees = () => {
    setAcceptParticipantsInitialTab("attending");
    setActiveView("attendance");
  };

  const handleOpenAcceptParticipantsDialog = () => {
    setAcceptParticipantsInitialTab("available");
    setActiveView("attendance");
  };

  const handleReplaceImage = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteImage = () => {
    if (!event?.id || !primaryImageFile?.id) return;
    if (event.recurrence) {
      setFileDeleteScope("single");
      setIsFileDeleteScopeDialogOpen(true);
      return;
    }
    setFileDeleteScope("single");
    setIsFileDeleteConfirmOpen(true);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !event?.id) return;

    setFileError(null);
    e.target.value = "";

    if (file.size > EVENT_IMAGE_MAX_FILE_SIZE) {
      setFileError(
        t("eventCreate.fileSizeError") || "File size must be less than 5MB",
      );
      return;
    }

    if (event.recurrence) {
      setPendingFile(file);
      setFileScope("single");
      setIsFileScopeDialogOpen(true);
      return;
    }

    try {
      await replaceFile(event.id, file);
    } catch (error) {
      console.error("Failed to replace image:", error);
    }
  };

  const handleCopyEventLink = async () => {
    if (!event?.id) return;

    try {
      await copyEventLinkToClipboard(event.code, t);
      toast({
        title: t("common.success"),
        description: t("common.linkCopied"),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("common.linkCopyFailed"),
        variant: "error",
      });
    }
  };

  const handleOpenParticipantScopeDialog = (
    type: "accept" | "decline",
    ids: number[],
  ) => {
    if (!ids || ids.length === 0) return;
    setParticipantScope("single");
    setPendingParticipantAction({ type, ids });
    setIsParticipantScopeDialogOpen(true);
  };

  const handleParticipantScopeConfirm = useCallback(
    async (scopeOverride?: RecurrenceScope) => {
      if (!pendingParticipantAction) return;
      const { type, ids } = pendingParticipantAction;
      const scopeToUse = scopeOverride ?? participantScope;

      if (type === "accept") {
        await handleAcceptParticipants(ids, scopeToUse);
      } else {
        await handleDeclineParticipants(ids, scopeToUse);
      }
      setIsParticipantScopeDialogOpen(false);
      setPendingParticipantAction(null);
    },
    [
      pendingParticipantAction,
      participantScope,
      handleAcceptParticipants,
      handleDeclineParticipants,
    ],
  );

  const handleParticipantScopeCancel = useCallback(() => {
    setIsParticipantScopeDialogOpen(false);
    setPendingParticipantAction(null);
  }, []);

  const handleDeleteScopeConfirm = useCallback(
    (scope: RecurrenceScope) => {
      setDeleteScope(scope);
      setIsDeleteScopeDialogOpen(false);
      if (!onDelete) return;
      onDelete(event, event, scope);
    },
    [onDelete, event],
  );

  const handleDeleteScopeCancel = useCallback(() => {
    setIsDeleteScopeDialogOpen(false);
  }, []);

  const withRecurrenceScopePrompt = (
    openScopeDialog: () => void,
    action: () => Promise<void>,
  ) => {
    if (event?.recurrence) {
      openScopeDialog();
      return;
    }
    void action();
  };

  const handleParticipantUpdateWithScope = async (
    type: "accept" | "decline",
    ids: number[],
  ): Promise<void> => {
    withRecurrenceScopePrompt(
      () => handleOpenParticipantScopeDialog(type, ids),
      async () => {
        if (type === "accept") {
          await handleAcceptParticipants(ids);
        } else {
          await handleDeclineParticipants(ids);
        }
      },
    );
  };

  const handleAttendClickWithScope = () => {
    withRecurrenceScopePrompt(
      () => {
        setAttendScope("single");
        setIsAttendScopeDialogOpen(true);
      },
      async () => {
        await handleAttendClick();
      },
    );
  };

  const handleAttendScopeConfirm = useCallback(
    async (scopeOverride?: RecurrenceScope) => {
      await handleAttendClick(scopeOverride);
      setIsAttendScopeDialogOpen(false);
    },
    [handleAttendClick],
  );

  const handleAttendScopeCancel = useCallback(() => {
    if (respondLoading) return;
    setIsAttendScopeDialogOpen(false);
  }, [respondLoading]);

  const handleFileScopeConfirm = useCallback(
    async (scope: RecurrenceScope) => {
      if (!pendingFile || !event?.id) return;
      setFileScope(scope);
      try {
        await replaceFile(event.id, pendingFile, scope);
      } catch (error) {
        console.error("Failed to replace image:", error);
      } finally {
        setPendingFile(null);
        setIsFileScopeDialogOpen(false);
      }
    },
    [pendingFile, event?.id, replaceFile],
  );

  const handleFileScopeCancel = useCallback(() => {
    if (replaceFileLoading) return;
    setPendingFile(null);
    setIsFileScopeDialogOpen(false);
  }, [replaceFileLoading]);

  const handleFileDeleteScopeConfirm = useCallback((scope: RecurrenceScope) => {
    setFileDeleteScope(scope);
    setIsFileDeleteScopeDialogOpen(false);
    setIsFileDeleteConfirmOpen(true);
  }, []);

  const handleFileDeleteScopeCancel = useCallback(() => {
    if (removeFileLoading) return;
    setIsFileDeleteScopeDialogOpen(false);
  }, [removeFileLoading]);

  const handleFileDeleteConfirm = useCallback(async () => {
    if (!event?.id || !primaryImageFile?.id) return;
    try {
      await removeFile(event.id, primaryImageFile.id, fileDeleteScope);
      setIsFileDeleteConfirmOpen(false);
    } catch (error) {
      console.error("Failed to remove image:", error);
    }
  }, [event?.id, primaryImageFile?.id, removeFile, fileDeleteScope]);

  const scopeSelectionModals = useMemo(
    () =>
      event?.recurrence
        ? [
            {
              key: "delete",
              isOpen: isDeleteScopeDialogOpen,
              onClose: handleDeleteScopeCancel,
              onConfirm: handleDeleteScopeConfirm,
              defaultScope: deleteScope,
              loading: false,
            },
            {
              key: "attend",
              isOpen: isAttendScopeDialogOpen,
              onClose: respondLoading ? () => {} : handleAttendScopeCancel,
              onConfirm: async (scope: RecurrenceScope) => {
                setAttendScope(scope);
                await handleAttendScopeConfirm(scope);
              },
              defaultScope: attendScope,
              loading: respondLoading,
            },
            {
              key: "participant",
              isOpen: isParticipantScopeDialogOpen,
              onClose: participantMutationLoading
                ? () => {}
                : handleParticipantScopeCancel,
              onConfirm: handleParticipantScopeConfirm,
              defaultScope: participantScope,
              loading: participantMutationLoading,
            },
            {
              key: "file",
              isOpen: isFileScopeDialogOpen,
              onClose: replaceFileLoading ? () => {} : handleFileScopeCancel,
              onConfirm: handleFileScopeConfirm,
              defaultScope: fileScope,
              loading: replaceFileLoading,
            },
            {
              key: "file-delete",
              isOpen: isFileDeleteScopeDialogOpen,
              onClose: removeFileLoading
                ? () => {}
                : handleFileDeleteScopeCancel,
              onConfirm: handleFileDeleteScopeConfirm,
              defaultScope: fileDeleteScope,
              loading: removeFileLoading,
            },
          ]
        : [],
    [
      event?.recurrence,
      isDeleteScopeDialogOpen,
      handleDeleteScopeCancel,
      handleDeleteScopeConfirm,
      deleteScope,
      isAttendScopeDialogOpen,
      respondLoading,
      handleAttendScopeCancel,
      attendScope,
      isParticipantScopeDialogOpen,
      participantMutationLoading,
      handleParticipantScopeCancel,
      participantScope,
      isFileScopeDialogOpen,
      replaceFileLoading,
      handleFileScopeCancel,
      fileScope,
      isFileDeleteScopeDialogOpen,
      removeFileLoading,
      handleFileDeleteScopeCancel,
      handleFileDeleteScopeConfirm,
      fileDeleteScope,
      handleParticipantScopeConfirm,
      handleAttendScopeConfirm,
      handleFileScopeConfirm,
    ],
  );

  const locationInfo = formatEventLocationDisplay(
    {
      locationType: event.location_type,
      location: event.location,
      onlineLocation: event.online_location,
      legacyLocation: event.location,
    },
    t,
  );
  const hasDescription = Boolean(event.description);
  const formattedDate = useMemo(
    () =>
      buildEventDateLabel(
        event,
        i18n.language,
        t("common.dateNotAvailable", "Date not available"),
      ),
    [event, i18n.language, t],
  );
  const activeScopeSelection = scopeSelectionModals.find(
    (modal) => modal.isOpen,
  );
  const isScopeSelectionView = Boolean(activeScopeSelection);
  const isAttendanceView =
    activeView === "attendance" && canManageEvent && !isScopeSelectionView;

  return (
    <div
      className={
        isAttendanceView || isScopeSelectionView
          ? "flex min-h-0 flex-col"
          : "flex min-h-0 flex-col gap-6 p-6"
      }
    >
      {activeScopeSelection ? (
        <ScopeSelectionContent
          isActive={activeScopeSelection.isOpen}
          onClose={activeScopeSelection.onClose}
          onConfirm={activeScopeSelection.onConfirm}
          defaultScope={activeScopeSelection.defaultScope}
          loading={activeScopeSelection.loading}
          scopes={scopeOptionsForModal}
          description={scopeModalDescription}
          scopeOptionLabels={scopeOptionLabels}
        />
      ) : isAttendanceView ? (
        <AcceptParticipantsContent
          isActive={activeView === "attendance"}
          studyGroupId={event.container?.id ?? null}
          onBack={() => setActiveView("detail")}
          onClose={() => setActiveView("detail")}
          onConfirmAccept={(ids) =>
            handleParticipantUpdateWithScope("accept", ids)
          }
          onConfirmDecline={(ids) =>
            handleParticipantUpdateWithScope("decline", ids)
          }
          isSubmitting={participantMutationLoading}
          excludedUserIds={attendingParticipants
            .map((p) => p.id)
            .filter((id): id is number => typeof id === "number")}
          attendingUsers={attendingParticipants}
          initialTab={acceptParticipantsInitialTab}
        />
      ) : (
        <>
          <EventHero
            event={event}
            isDraft={isDraft}
            isOwner={isOwner}
            imageLoading={imageLoading}
            imageError={imageError}
            fileBlobUrl={fileBlobUrl}
            replaceFileLoading={replaceFileLoading}
            removeFileLoading={removeFileLoading}
            fileError={fileError}
            replaceFileError={replaceFileError}
            removeFileError={removeFileError}
            onReplaceImage={handleReplaceImage}
            onDeleteImage={handleDeleteImage}
            onFileChange={handleFileChange}
            onCopyEventLink={handleCopyEventLink}
            fileInputRef={fileInputRef}
            onStudyGroupClick={onStudyGroupClick}
            isInteractionDisabled={respondLoading}
          />

          <div className="flex gap-5">
            {hasDescription ? (
              <EventDescription
                description={event.description}
                showFullDescription={showFullDescription}
                onToggle={() => setShowFullDescription(!showFullDescription)}
                isInteractionDisabled={respondLoading}
              />
            ) : (
              <div className="flex-1" aria-hidden />
            )}

            <EventSidebar
              formattedDate={formattedDate}
              formatLabel={locationInfo.type}
              location={event.location}
              onlineLocation={event.online_location}
              showLocationDetails={showLocationDetails}
            />
          </div>

          {canManageEvent && (
            <>
              <ReminderEmailDialog
                open={isReminderEmailDialogOpen}
                event={event}
                participants={attendingParticipants}
                primaryImageGuid={primaryImageGuid}
                onOpenChange={setIsReminderEmailDialogOpen}
                onDirtyChange={setHasReminderUnsavedChanges}
                hasEmailUnsavedChanges={hasReminderUnsavedChanges}
                onShowUnsavedWarning={() => setShowReminderUnsavedWarning(true)}
                onRemindersUpdated={async (scope) => {
                  const applyScope = scope ?? "single";
                  if (event.recurrence) {
                    await propagateReminders(event.id, applyScope);
                  }
                  await onDataRefresh?.();
                }}
                hasRecurrence={Boolean(event.recurrence)}
              />
              <SendEmailNowDialog
                open={isSendEmailNowDialogOpen}
                event={event}
                participants={attendingParticipants}
                primaryImageGuid={primaryImageGuid}
                onOpenChange={setIsSendEmailNowDialogOpen}
                onDirtyChange={setHasSendEmailUnsavedChanges}
                hasEmailUnsavedChanges={hasSendEmailUnsavedChanges}
                onShowUnsavedWarning={() =>
                  setShowSendEmailUnsavedWarning(true)
                }
              />
            </>
          )}
        </>
      )}
      <ConfirmationDialog
        open={showReminderUnsavedWarning}
        onOpenChange={setShowReminderUnsavedWarning}
        title={t("eventManage.reminderEmailUnsavedTitle")}
        description={t("eventManage.reminderEmailUnsavedDescription")}
        cancelText={t("common.cancel")}
        confirmText={t("eventManage.reminderEmailDiscardButton")}
        onConfirm={() => {
          setShowReminderUnsavedWarning(false);
          setHasReminderUnsavedChanges(false);
          setIsReminderEmailDialogOpen(false);
        }}
      />
      <ConfirmationDialog
        open={showSendEmailUnsavedWarning}
        onOpenChange={setShowSendEmailUnsavedWarning}
        title={t("eventManage.reminderEmailUnsavedTitle")}
        description={t("eventManage.reminderEmailUnsavedDescription")}
        cancelText={t("common.cancel")}
        confirmText={t("eventManage.reminderEmailDiscardButton")}
        onConfirm={() => {
          setShowSendEmailUnsavedWarning(false);
          setHasSendEmailUnsavedChanges(false);
          setIsSendEmailNowDialogOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isFileDeleteConfirmOpen}
        onOpenChange={setIsFileDeleteConfirmOpen}
        title={t("eventManage.deleteEventImage")}
        description={t("eventManage.deleteEventImageConfirm")}
        cancelText={t("common.cancel")}
        confirmText={t("common.delete")}
        onConfirm={() => {
          void handleFileDeleteConfirm();
        }}
        isLoading={removeFileLoading}
        variant="destructive"
      />
      {activeView === "detail" &&
        !isScopeSelectionView &&
        !hideFooter &&
        (renderFooter ? (
          renderFooter(event)
        ) : (
          <DefaultFooter
            canManageEvent={canManageEvent}
            shouldRenderAttendCta={shouldRenderAttendCta}
            attendingParticipants={attendingParticipants}
            uniqueReminders={uniqueReminders}
            isDraft={isDraft}
            showDuplicateButton={showDuplicateButton}
            handleAttendClick={handleAttendClickWithScope}
            handleShowAttendees={handleShowAttendees}
            handleOpenSelectAttendees={handleOpenAcceptParticipantsDialog}
            handleDuplicate={handleDuplicate}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleOpenAutoNotifications={() =>
              setIsReminderEmailDialogOpen(true)
            }
            handleOpenSendEmailNow={() => setIsSendEmailNowDialogOpen(true)}
            respondLoading={respondLoading}
            respondError={respondError}
            isAttending={isAttending}
            isInteractionDisabled={respondLoading}
            acceptAllParticipantsLoading={acceptAllParticipantsLoading}
            canBulkAcceptParticipants={canBulkAcceptParticipants}
          />
        ))}
    </div>
  );
};
